'use client'

import { useEffect, useRef, useState } from "react"
import Image from "next/image"
import { AnimatePresence, motion, useInView } from "framer-motion"
import { Check } from "lucide-react"

import MobicomLogo from "@/assets/mobicom.png"
import MobicomLogoDark from "@/assets/mobicom-dark.png"
import UnitelLogo from "@/assets/unitel.svg"
import UnitelLogoDark from "@/assets/unitel-dark.svg"
import SkytelLogo from "@/assets/skytel.svg"
import SkytelLogoDark from "@/assets/skytel-dark.svg"
import GmobileLogo from "@/assets/gmobile.svg"
import GmobileLogoDark from "@/assets/gmobile-dark.svg"
import OndoLogo from "@/assets/ondo.svg"
import OndoLogoDark from "@/assets/ondo-dark.svg"
import LimeLogo from "@/assets/lime.svg"
import LimeLogoDark from "@/assets/lime-dark.svg"
import TokiLogo from "@/assets/toki.svg"
import TokiLogoDark from "@/assets/toki-dark.svg"
import KaosLogo from "@/assets/kaos.svg"
import KaosLogoDark from "@/assets/kaos-dark.svg"

import { cn } from "@/lib/util"
import { SoftBadge, formatCredits, getBatchStatusBadge, getMessageStatusBadge } from "@/lib/wesend"
import { useMotionAllowed } from "./motion"

// The small moving pictures inside the feature cards. Each one plays only while it is on screen.

function usePlaying(ref: React.RefObject<HTMLElement | null>) {
  const allowed = useMotionAllowed()
  const inView = useInView(ref, { margin: '-10% 0px' })
  return { allowed, playing: allowed && inView }
}

/* ============== SMS: the operator is read from the number ============== */

// Every operator WeSend reaches, with a number of theirs. Toki's numbers are on Unitel's network
// and KAOS's on Mobicom's (the backend's lib/carrier.js). The logos are cropped to their edges
// and differ in shape, so each gets the height at which it looks as large as the others.
const operators = [
  { name: 'Mobicom', prefix: '99', rest: '11 2233', light: MobicomLogo, dark: MobicomLogoDark, size: 'h-[28px]' },
  { name: 'Unitel', prefix: '88', rest: '11 2233', light: UnitelLogo, dark: UnitelLogoDark, size: 'h-[14px]' },
  { name: 'Skytel', prefix: '90', rest: '11 2233', light: SkytelLogo, dark: SkytelLogoDark, size: 'h-[24px]' },
  { name: 'G-Mobile', prefix: '98', rest: '11 2233', light: GmobileLogo, dark: GmobileLogoDark, size: 'h-[18px]' },
  { name: 'ONDO', prefix: '66', rest: '11 2233', light: OndoLogo, dark: OndoLogoDark, size: 'h-[15px]' },
  { name: 'Lime', prefix: '72', rest: '11 2233', light: LimeLogo, dark: LimeLogoDark, size: 'h-[24px]' },
  { name: 'Toki', prefix: '5511', rest: ' 2233', light: TokiLogo, dark: TokiLogoDark, size: 'h-[21px]' },
  { name: 'KAOS', prefix: '505', rest: '1 2233', light: KaosLogo, dark: KaosLogoDark, size: 'h-[12px]' },
]

// The list's rows and the gaps between them keep a 5 to 1 ratio at every screen size, so the
// lines are drawn in those units: a row is 5 tall, a gap 1, and a line ends at its row's middle.
const ROW = 5
const GAP = 1
const LINES_HEIGHT = operators.length * ROW + (operators.length - 1) * GAP

function lineTo(position: number) {
  const from = LINES_HEIGHT / 2
  const to = ROW / 2 + position * (ROW + GAP)
  return `M0 ${from} C55 ${from} 45 ${to} 100 ${to}`
}

export function OperatorRouting() {
  const ref = useRef<HTMLDivElement>(null)
  const { playing } = usePlaying(ref)
  const [index, setIndex] = useState(0)

  useEffect(() => {
    if(!playing) return
    const timer = setInterval(() => setIndex(current => (current + 1) % operators.length), 2000)
    return () => clearInterval(timer)
  }, [playing])

  const current = operators[index]

  return (
    <div ref={ref} className="mx-auto flex w-full max-w-md">
      <div aria-hidden className="relative w-[7.5rem] shrink-0 self-center sm:w-40 lg:w-32 xl:w-40">
        <span className="absolute -inset-10 -z-10 rounded-full bg-linear-to-br from-blue-500/25 to-indigo-400/10 blur-2xl" />
        <div className="rounded-xl border bg-background px-3 py-3 sm:px-4 lg:px-3 xl:px-4">
          <p className="text-xs text-muted-foreground">Хүлээн авагч</p>
          <p className="mt-1 h-7 overflow-hidden text-[15px] font-semibold leading-7 tabular-nums sm:text-xl lg:text-base xl:text-xl">
            <AnimatePresence mode="wait" initial={false}>
              <motion.span key={current.name} className="block" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
                <span className="text-blue">{current.prefix}</span>{current.rest}
              </motion.span>
            </AnimatePresence>
          </p>
        </div>
      </div>

      {/* A line from the number to every operator. The one its prefix belongs to lights up. */}
      <div aria-hidden className="relative min-w-6 flex-1 self-stretch">
        <svg viewBox={`0 0 100 ${LINES_HEIGHT}`} preserveAspectRatio="none" fill="none" className="absolute inset-0 size-full">
          <defs>
            <motion.linearGradient key={index} id="operator-beam" gradientUnits="userSpaceOnUse" y1="0" y2="0"
              initial={{ x1: -60, x2: 0 }} animate={{ x1: 100, x2: 160 }} transition={{ duration: 0.9, ease: 'easeOut' }}>
              <stop offset="0" stopColor="#3b82f6" stopOpacity="0" />
              <stop offset="0.8" stopColor="#3b82f6" />
              <stop offset="1" stopColor="#818cf8" stopOpacity="0" />
            </motion.linearGradient>
          </defs>
          {operators.map((operator, position) => (
            <path key={operator.name} d={lineTo(position)} strokeWidth={1} vectorEffect="non-scaling-stroke"
              className={cn("transition-[stroke] duration-500", position === index ? 'stroke-blue/45' : 'stroke-border')} />
          ))}
          {playing && <path d={lineTo(index)} stroke="url(#operator-beam)" strokeWidth={2} strokeLinecap="round" vectorEffect="non-scaling-stroke" />}
        </svg>
      </div>

      <ul aria-label="Дэмжигдэх операторууд" className="flex w-[8.25rem] shrink-0 flex-col gap-2 sm:w-44 lg:w-36 lg:gap-2.5 xl:w-48">
        {operators.map((operator, position) => (
          <li key={operator.name} className={cn("relative flex h-10 items-center gap-2 rounded-lg border bg-background px-3 transition-colors duration-300 lg:h-[50px]",
            position === index ? 'border-blue/50' : '')}>
            {position === index && <motion.span layoutId="operator-active" className="absolute inset-0 rounded-[7px] bg-blue/10" transition={{ type: 'spring', stiffness: 360, damping: 32 }} />}
            <span className="relative flex min-w-0 flex-1 items-center">
              <Image src={operator.light} alt={operator.name} sizes="96px"
                className={cn("w-auto max-w-full object-contain object-left transition-opacity duration-300 dark:hidden", operator.size, position === index ? 'opacity-100' : 'opacity-70')} />
              <Image src={operator.dark} alt={operator.name} sizes="96px"
                className={cn("hidden w-auto max-w-full object-contain object-left transition-opacity duration-300 dark:block", operator.size, position === index ? 'opacity-100' : 'opacity-70')} />
            </span>
            {/* Always there, so a logo keeps its size when its row lights up. */}
            <span aria-hidden className={cn("relative shrink-0 text-[11px] font-semibold tabular-nums text-blue transition-opacity duration-300", position === index ? 'opacity-100' : 'opacity-0')}>{operator.prefix}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/* ============== Verify: the code arrives, is typed and accepted ============== */

const CODE = '482913'

export function CodeCheck() {
  const ref = useRef<HTMLDivElement>(null)
  const { allowed, playing } = usePlaying(ref)
  const [count, setCount] = useState(0)
  const [approved, setApproved] = useState(false)
  const [round, setRound] = useState(0)

  useEffect(() => {
    if(!playing) return
    let timer: ReturnType<typeof setTimeout>
    if(count < CODE.length) timer = setTimeout(() => setCount(count + 1), count === 0 ? 1100 : 300)
    else if(!approved) timer = setTimeout(() => setApproved(true), 450)
    else timer = setTimeout(() => { setCount(0); setApproved(false); setRound(current => current + 1) }, 2600)
    return () => clearTimeout(timer)
  }, [playing, count, approved])

  const shown = allowed ? count : CODE.length
  const accepted = allowed ? approved : true

  return (
    <div ref={ref} aria-hidden>
      {/* The SMS the user receives. It arrives again at the start of every round. */}
      <motion.div key={round} initial={round > 0 ? { opacity: 0, y: 8 } : false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: 'easeOut' }}
        className="w-fit max-w-full rounded-2xl rounded-bl-md border bg-background px-3.5 py-2.5">
        <p className="flex items-baseline gap-2 text-xs">
          <span className="font-semibold">Shop.mn</span>
          <span className="text-muted-foreground">одоо</span>
        </p>
        <p className="mt-0.5 text-[13px] leading-5">Таны баталгаажуулах код <span className="font-semibold tabular-nums">{CODE}</span></p>
      </motion.div>

      <div className="mt-5 flex gap-1.5">
        {CODE.split('').map((digit, position) => (
          <span key={position} className={cn("flex h-12 min-w-0 flex-1 items-center justify-center rounded-lg border bg-background text-xl font-semibold tabular-nums transition-colors duration-300",
            accepted ? 'border-success/50 text-success' : position === shown ? 'border-blue/60' : '')}>
            {position < shown ? digit : ''}
          </span>
        ))}
      </div>
      <div className="mt-3 flex h-6 items-center">
        {accepted
          ? <SoftBadge tone="success"><Check className="size-3" />Баталгаажлаа</SoftBadge>
          : <span className="text-xs text-muted-foreground">Код 5 минут хүчинтэй</span>}
      </div>
    </div>
  )
}

/* ============== Email: what lands in the inbox ============== */

// The letter fills what is left of its card and fades out at the bottom, so its length never
// sets the card's height.
export function EmailPreview({ className = '' }: { className?: string }) {
  return (
    <div aria-hidden className={cn("relative min-h-56 flex-1", className)}>
      <div className="absolute inset-0 overflow-hidden rounded-t-xl border border-b-0 bg-background [mask-image:linear-gradient(to_bottom,black_70%,transparent)]">
        <div className="flex items-center gap-3 border-b px-4 py-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-blue/10 font-gilroy text-sm font-semibold text-blue">S</span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold leading-5">Shop.mn</p>
            <p className="truncate text-xs leading-4 text-muted-foreground">Хариу авах: info@shop.mn</p>
          </div>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">09:41</span>
        </div>
        <div className="px-4 py-3.5">
          <p className="text-sm font-semibold">Таны захиалга баталгаажлаа</p>
          <p className="mt-1.5 text-[13px] leading-5 text-muted-foreground">Сайн байна уу, Бат. #1042 дугаартай захиалгыг тань хүлээн авлаа.</p>
          <span className="bg-gradient mt-3 inline-flex rounded-md px-3 py-1.5 text-xs font-semibold text-white">Захиалга харах</span>
          <p className="mt-3 text-[13px] leading-5 text-muted-foreground">Асуух зүйл гарвал энэ имэйлд хариу бичээрэй.</p>
        </div>
      </div>
    </div>
  )
}

/* ============== Dashboard: a message written, sent and followed ============== */

const TOTAL = 500
const FAILED = 3
const MESSAGE = 'Shine baraa irlee! shop.mn'
// How many of the recipients the compose side names before "+497".
const NAMED = 3

// Each recipient's message is sent once the batch has come this far.
const recipients = [
  { number: '9911 2233', operator: 'Mobicom', at: 25 },
  { number: '8811 2233', operator: 'Unitel', at: 50 },
  { number: '9011 2233', operator: 'Skytel', at: 75 },
  { number: '9811 2233', operator: 'G-Mobile', at: 100 },
]

// The dashboard's window, cut off by the bottom edge of its card.
export function DashboardSend({ smsCredits, className = '' }: { smsCredits: number, className?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const { allowed, playing } = usePlaying(ref)
  const [percent, setPercent] = useState(0)

  useEffect(() => {
    if(!playing) return
    const timer = percent < 100
      ? setTimeout(() => setPercent(Math.min(100, percent + 2)), 70)
      : setTimeout(() => setPercent(0), 3200)
    return () => clearTimeout(timer)
  }, [playing, percent])

  const progress = allowed ? percent : 100
  const finished = progress >= 100
  const handled = Math.round(TOTAL * progress / 100)
  const sent = Math.max(0, handled - (finished ? FAILED : 0))

  return (
    <div ref={ref} aria-hidden className={cn("grid grid-cols-1 divide-y overflow-hidden rounded-t-xl border border-b-0 bg-background md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:divide-x md:divide-y-0", className)}>
      {/* What the sender fills in. */}
      <div className="flex flex-col p-4">
        <p className="text-xs font-medium text-muted-foreground">Хүлээн авагч</p>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {recipients.slice(0, NAMED).map(recipient => (
            <span key={recipient.number} className="rounded-md bg-muted px-2 py-1 text-xs tabular-nums">{recipient.number}</span>
          ))}
          <span className="px-1 text-xs font-medium text-muted-foreground tabular-nums">+{TOTAL - NAMED}</span>
        </div>

        <p className="mt-4 text-xs font-medium text-muted-foreground">Мессеж</p>
        <p className="mt-2 min-h-16 flex-1 rounded-lg border px-3 py-2 text-sm">{MESSAGE}</p>

        <div className="mt-3 flex items-center justify-between gap-3">
          <span className="min-w-0 truncate text-xs tabular-nums text-muted-foreground">{formatCredits(TOTAL)} SMS, {formatCredits(TOTAL * smsCredits)} кредит</span>
          <span className="bg-gradient shrink-0 rounded-md px-3.5 py-1.5 text-xs font-semibold text-white">Илгээх</span>
        </div>
      </div>

      {/* What the dashboard shows while the batch goes out. */}
      <div className="flex flex-col p-4 pb-0">
        <div className="flex items-center justify-between gap-3">
          <p className="min-w-0 truncate text-xs font-medium text-muted-foreground">Илгээлтийн явц</p>
          {getBatchStatusBadge(finished ? 'completed' : 'processing')}
        </div>
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-muted">
          <div className="bg-gradient h-full rounded-full" style={{ width: `${progress}%` }} />
        </div>
        <div className="mt-2.5 flex min-h-5 flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="tabular-nums"><span className="font-semibold text-foreground">{formatCredits(sent)}</span> / {formatCredits(TOTAL)} илгээгдсэн</span>
          {/* Always there, so the row is as tall before the batch ends as after it. */}
          <span className={finished ? '' : 'invisible'}>{FAILED} амжилтгүй, кредит буцсан</span>
        </div>

        <ul className="mt-3 divide-y border-t [mask-image:linear-gradient(to_bottom,black_60%,transparent)]">
          {recipients.map(recipient => (
            <li key={recipient.number} className="flex h-9 items-center gap-3 text-xs">
              <span className="shrink-0 font-medium tabular-nums">{recipient.number}</span>
              <span className="min-w-0 flex-1 truncate text-muted-foreground">{recipient.operator}</span>
              {getMessageStatusBadge(progress >= recipient.at ? 'sent' : progress >= recipient.at - 25 ? 'processing' : 'queued')}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
